import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  getLandlord,
  getRider,
  getProperty,
  createClaim,
  checkPolicyDuplicated,
  getTenants,
  lookupPolicy,
} from './APIs/claim';
import { createClaimStaged } from './APIs/claimUpload';
import { isStagedUploadEnabled } from 'utils/stagedUpload';

import types from '../constants/claimformConstants';

import {
  setAgreementIdAction,
  setLandlordArrayAction,
  setErrorAction,
  setLedgerIdAction,
  setClaimIdAction,
  setApartmentArrayAction,
  setRiderArrayAction,
  setPolicyDuplicatedAction,
  setResultAction,
  setTenantArrayAction,
  setTenants,
  setTenantsLoading,
} from '../actions/claimFormActions';
import { getApiErrorMessage } from 'utils/apiError';
import {
  CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE,
  CLAIM_NETWORK_ERROR_MESSAGE,
  exceedsClaimDocumentsSizeLimit,
  isClaimNetworkError,
} from 'utils/claimUpload';

function* getLandlordSaga({ payload }) {
  try {
    const property = yield getLandlord(payload);
    yield put(setLandlordArrayAction(property));
  } catch (error) {
    yield put(
      setErrorAction(getApiErrorMessage(error, 'Unable to load companies. Please try again.')),
    );
  }
}

function* onGetLandlord() {
  yield takeLatest(types.GET_LANDLORD, getLandlordSaga);
}

function* getApartmentSaga({ payload }) {
  try {
    const property = yield getProperty(payload);
    yield put(setApartmentArrayAction(property));
  } catch (error) {
    yield put(
      setErrorAction(getApiErrorMessage(error, 'Unable to load properties. Please try again.')),
    );
  }
}

function* onGetApartment() {
  yield takeLatest(types.GET_APARTMENT, getApartmentSaga);
}

function normalizeCreateClaimError(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return { message: 'Unable to submit claim.' };
  }

  if (responseData.message === 'isDuplicated') {
    return {
      message: 'isDuplicated',
      claimData: responseData.claimData || responseData.data || [],
    };
  }

  const duplicateClaims = Array.isArray(responseData.data) ? responseData.data : null;
  if (duplicateClaims?.length > 0 && duplicateClaims[0]?.Name) {
    return { message: 'isDuplicated', claimData: duplicateClaims };
  }

  return responseData;
}

function* createClaimSaga({ payload }) {
  const useStaged = isStagedUploadEnabled();

  if (useStaged && payload.uploadSessionId) {
    const { uploadSessionId, leaseFile, ledgerFile, otherFiles, ...claimData } = payload;
    try {
      yield put(setResultAction({ status: 'pending' }));
      const res = yield call(createClaimStaged, { claimData, uploadSessionId });
      if (res.name) {
        yield put(setClaimIdAction(res.name));
      }
      yield put(
        setResultAction({
          status: 'success',
          id: res.id || null,
          name: res.name || null,
          uploadSessionId: res.uploadSessionId || uploadSessionId,
          documentSyncStatus: res.documentSyncStatus,
          documentsTotal: res.documentsTotal,
          documentsUploaded: res.documentsUploaded || 0,
        }),
      );
    } catch (error) {
      const message = isClaimNetworkError(error)
        ? CLAIM_NETWORK_ERROR_MESSAGE
        : getApiErrorMessage(
            error,
            'Unable to submit your claim. Please try again or contact claims@leapeasy.com.',
          );
      const errorData = error?.response?.data?.data;
      yield put(
        setResultAction({
          status: 'failed',
          message,
          data: errorData,
        }),
      );
    }
    return;
  }

  const { leaseFile, ledgerFile, otherFiles, ...claimData } = payload;
  try {
    if (exceedsClaimDocumentsSizeLimit({ leaseFile, ledgerFile, otherFiles })) {
      yield put(
        setResultAction({
          status: 'failed',
          message: CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE,
          data: { message: CLAIM_DOCUMENTS_TOO_LARGE_MESSAGE },
        }),
      );
      return;
    }

    const formData = new FormData();
    formData.append('claimData', JSON.stringify(claimData));
    formData.append(
      'files',
      ledgerFile,
      `Lease Agreement Document.${ledgerFile.path.split('.')[1]}`,
    );
    formData.append('files', leaseFile, `Resident Ledger Document.${leaseFile.path.split('.')[1]}`);
    for (const otherFile of otherFiles) {
      formData.append('files', otherFile, otherFile.path);
    }
    yield put(setResultAction({ status: 'pending' }));
    const res = yield createClaim(formData);
    yield put(setClaimIdAction(res.name));
    yield put(setResultAction({ status: 'success', id: res.id, name: res.name }));
  } catch (error) {
    const message = isClaimNetworkError(error)
      ? CLAIM_NETWORK_ERROR_MESSAGE
      : getApiErrorMessage(
          error,
          'Unable to submit your claim. Please try again or contact claims@leapeasy.com.',
        );
    yield put(
      setResultAction({
        status: 'failed',
        message,
        data: normalizeCreateClaimError(error.response?.data),
      }),
    );
  }
}

function* onCreateClaim() {
  yield takeLatest(types.CREAT_CLAIM, createClaimSaga);
}

function* getRiderSaga({ payload }) {
  try {
    const riders = yield getRider(payload);
    yield put(setRiderArrayAction(riders));
  } catch (error) {
    yield put(
      setErrorAction(getApiErrorMessage(error, 'Unable to load policies. Please try again.')),
    );
  }
}

function* getTenantsSaga({ payload }) {
  try {
    yield put(setTenantsLoading(true));
    const tenants = yield getTenants(payload);
    yield put(setTenants(tenants));
  } catch (error) {
    yield put(setTenantsLoading(false));
    yield put(
      setErrorAction(
        getApiErrorMessage(error, 'Unable to load tenant information. Please try again.'),
      ),
    );
  }
}

function* onGetTenants() {
  yield takeLatest(types.GET_TENANTS, getTenantsSaga);
}

function* lookupPolicySaga({ payload }) {
  try {
    yield put(setPolicyDuplicatedAction(null));
    yield put(setTenants([]));
    const result = yield lookupPolicy(payload);
    return { status: 'success', data: result };
  } catch (error) {
    const message = getApiErrorMessage(
      error,
      'No matching policy found. Please check your policy number and try again.',
    );
    yield put(setErrorAction(message));
    return { status: 'failed', message };
  }
}

function* onLookupPolicy() {
  yield takeLatest(types.LOOKUP_POLICY, lookupPolicySaga);
}

function* onGetRider() {
  yield takeLatest(types.GET_RIDER, getRiderSaga);
}

function* checkDuplicatedSaga({ payload }) {
  try {
    yield put(setPolicyDuplicatedAction(null));
    const riderId = typeof payload === 'string' ? payload : payload?.riderId;
    const result = yield checkPolicyDuplicated(riderId);
    yield put(setPolicyDuplicatedAction(result));
  } catch (error) {
    yield put(
      setErrorAction(getApiErrorMessage(error, 'Unable to check policy status. Please try again.')),
    );
  }
}

function* onCheckDuplicated() {
  yield takeLatest(types.CHECK_POLICY_DUPLICATED, checkDuplicatedSaga);
}

export default function* inviteSaga() {
  yield all([
    call(onGetLandlord),
    call(onGetApartment),
    call(onGetRider),
    call(onGetTenants),
    call(onLookupPolicy),
    call(onCreateClaim),
    call(onCheckDuplicated),
  ]);
}
